import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProfileApiResponse,
  getProfileDetails,
  updateProfileDetails,
  changePassword,
} from "./profileAction";

interface ProfileState {
  profile: {
    firstname?: string;
    lastname?: string;
    email: string;
    mobileNo?: string;
    emailNotification?: boolean;
    planName?: string;
    siteReportUsed?: number;
    siteReportRemaining?: number;
    fleetReportUsed?: number;
    fleetReportRemaining?: number;
    profileImg?: string;
    planExpiryDate?: string;
  } | null;
  loading: boolean;
  error: string | null;
  changePasswordLoading: boolean;
  changePasswordError: string | null;
}

const initialState: ProfileState = {
  profile: null,
  loading: false,
  error: null,
  changePasswordLoading: false,
  changePasswordError: null,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    resetProfileState: (state) => {
      state.profile = null;
      state.error = null;
      state.loading = false;
      state.changePasswordLoading = false;
      state.changePasswordError = null;
    },
    resetChangePasswordState: (state) => {
      state.changePasswordLoading = false;
      state.changePasswordError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getProfileDetails
      .addCase(getProfileDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getProfileDetails.fulfilled,
        (state, action: PayloadAction<ProfileApiResponse>) => {
          state.loading = false;
          state.profile = action.payload.data?.user || null;
          state.error = null;
        }
      )
      .addCase(
        getProfileDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to retrieve profile details";
        }
      )
      // Handle updateProfileDetails
      .addCase(updateProfileDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateProfileDetails.fulfilled,
        (state, action: PayloadAction<ProfileApiResponse>) => {
          state.loading = false;
          state.profile = action.payload.data?.user || null;
          state.error = null;
        }
      )
      .addCase(
        updateProfileDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update profile details";
        }
      )
      // Handle changePassword
      .addCase(changePassword.pending, (state) => {
        state.changePasswordLoading = true;
        state.changePasswordError = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.changePasswordLoading = false;
        state.changePasswordError = null;
      })
      .addCase(
        changePassword.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.changePasswordLoading = false;
          state.changePasswordError =
            action.payload || "Failed to change password";
        }
      );
  },
});

export const { resetProfileState, resetChangePasswordState } =
  profileSlice.actions;
export default profileSlice.reducer;
